import React from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { Card } from '../components/ui/card';
import { DollarSign, BarChart2, Shield, Zap, Calculator, GitCompare, FolderOpen, Network } from 'lucide-react';

const Widget = ({ title, description, icon, link, comingSoon }) => (
  <Link to={link}>
    <Card className={`flex flex-col p-6 h-full transition-colors ${comingSoon ? 'hover:bg-gray-50 opacity-80' : 'hover:bg-teal-50'}`}>
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      </div>
      {description && <p className="text-sm text-gray-500">{description}</p>}
      {comingSoon && (
        <span className="mt-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded w-fit">Coming Soon</span>
      )}
    </Card>
  </Link>
);

const Dashboard = () => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const userName = activeAccount ? (activeAccount.name || activeAccount.username || 'User') : 'User';

  return (
    <div className="container mx-auto p-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">
          Welcome, {userName} ☄️
        </h1>
        <p className="text-gray-600 mt-1">Cloud Operations Management &amp; Engineering Tool</p>
      </div>

      <h2 className="text-lg font-semibold text-gray-700 mb-3">Pricing Tools</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        <Widget
          title="Pricing Tool"
          description="Look up live Azure retail prices for VMs, storage, disks, and more."
          icon={<DollarSign size={24} className="text-teal-500" />}
          link="/pricing"
        />
        <Widget
          title="Cost Estimator"
          description="Build a multi-resource estimate and export to CSV."
          icon={<Calculator size={24} className="text-teal-500" />}
          link="/pricing/estimator"
        />
        <Widget
          title="Compare Regions"
          description="Compare prices for the same SKU across regions."
          icon={<GitCompare size={24} className="text-teal-500" />}
          link="/pricing/compare"
        />
        <Widget
          title="Saved Estimates"
          description="View and manage your saved cost estimates."
          icon={<FolderOpen size={24} className="text-teal-500" />}
          link="/pricing/saved"
        />
        <Widget
          title="Analytics"
          description="Explore saved estimate trends, spend mix, and regional footprint."
          icon={<BarChart2 size={24} className="text-teal-500" />}
          link="/analytics"
        />
        <Widget
          title="Landing Zone Designer"
          description="Visually design Azure landing zones and export Terraform code."
          icon={<Network size={24} className="text-teal-500" />}
          link="/designer"
        />
      </div>

      <h2 className="text-lg font-semibold text-gray-700 mb-3">Coming Soon</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Widget
          title="Security"
          description="Review and manage your Azure security posture."
          icon={<Shield size={24} className="text-red-400" />}
          link="/security"
          comingSoon
        />
        <Widget
          title="Performance"
          description="Monitor and optimise your cloud resource performance."
          icon={<Zap size={24} className="text-yellow-400" />}
          link="/performance"
          comingSoon
        />
      </div>
    </div>
  );
};

export default Dashboard;
